import { useEffect, useMemo } from 'react';
import { useCheckLoanApplication } from '@axo/shared/data-access/hooks/services/lender_filter/useCheckLoanApplication';
import { useLenders, useLoanApplication } from '@axo/shared/data-access/hooks';
import { Bank, type BankCode } from '@axo/ui-content/features/Partners';
import type { lender } from '@axo/shared/data-access/types';
import styles from './ExtraApplicationInfoCard.module.css';
import type { MarketCountryCode } from '@axo/shared/types';
import { Tooltip } from '@blueprintjs/core';

interface LenderLogoTableProps {
  applicationID: string;
}

export const LenderLogoTable: React.FC<LenderLogoTableProps> = ({
  applicationID,
}) => {
  const { data: applicationData } = useLoanApplication(applicationID);
  const {
    data: lenderEligibility,
    mutate: checkLoanApplication,
    isLoading,
  } = useCheckLoanApplication();

  useEffect(() => {
    if (applicationData) {
      checkLoanApplication({ application: applicationData });
    }
  }, [applicationData, checkLoanApplication]);

  const marketCountry = applicationData?.MarketCountry;

  const eligibleLenderIDs = (lenderEligibility?.EligibleLenders ?? []).map(
    (l) => l.ID,
  );
  const ineligibleLenderIDs = lenderEligibility?.FilteredLenders
    ? Object.keys(lenderEligibility.FilteredLenders)
    : [];
  const { data: lenders } = useLenders(marketCountry);

  const lendersByID = useMemo(() => {
    const map = new Map<string, lender.Lender>();
    (lenders ?? []).forEach((l) => {
      map.set(l.ID, l);
    });
    return map;
  }, [lenders]);

  const rowCount = Math.max(
    eligibleLenderIDs.length,
    ineligibleLenderIDs.length,
  );

  if (isLoading) {
    return <div>Loading eligible lenders...</div>;
  }

  if (!lenderEligibility) {
    return <div>Error when loading eligible lenders</div>;
  }

  return (
    <table className={styles.lenderTable}>
      <thead>
        <tr>
          <th className={styles.lenderHeader}>Eligible lenders</th>
          <th className={styles.lenderHeader}>Ineligible lenders</th>
        </tr>
      </thead>
      <tbody>
        {rowCount === 0 ? (
          <tr>
            <td className={styles.lenderCell}>
              <strong>-</strong>
            </td>
            <td className={styles.lenderCell}>
              <strong>-</strong>
            </td>
          </tr>
        ) : (
          Array.from({ length: rowCount }).map((_, rowIndex) => {
            const eligibleID = eligibleLenderIDs[rowIndex];
            const ineligibleID = ineligibleLenderIDs[rowIndex];

            const eligibleLender = lendersByID.get(eligibleID);
            const ineligibleLender = lendersByID.get(ineligibleID);
            const filterResults =
              lenderEligibility.FilteredLenders?.[ineligibleID] ?? [];

            return (
              <tr key={`row-${eligibleID}-${ineligibleID}`}>
                <td className={styles.lenderCell}>
                  {eligibleLender && marketCountry ? (
                    <Bank
                      code={eligibleLender.ShortName as BankCode}
                      name={eligibleLender.Name}
                      country={marketCountry as MarketCountryCode}
                      className={styles.lenderLogo}
                    />
                  ) : null}
                </td>
                <td className={styles.lenderCell}>
                  {ineligibleLender && marketCountry ? (
                    <Tooltip
                      content={
                        <div>
                          <p>
                            <strong>Lender: </strong>
                            {ineligibleLender.Name}
                          </p>
                          <p>
                            <strong>Lender Filter Results:</strong>
                          </p>
                          {filterResults.length > 0 ? (
                            <ul>
                              {filterResults.map((result) => (
                                <li key={result.ID}>{result.Result}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      }
                      placement="right"
                      renderTarget={(target) => (
                        <div {...target}>
                          <Bank
                            code={ineligibleLender.ShortName as BankCode}
                            name={ineligibleLender.Name}
                            country={marketCountry as MarketCountryCode}
                            className={styles.lenderLogo}
                          />
                        </div>
                      )}
                    />
                  ) : null}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
};
